import { useState } from 'react';

import {
  ColorsV2,
  FieldLabel,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Spacer,
  Text,
  TextInput,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useWipeDeviceCompat } from '@/hooks/useWipeDeviceCompat';
import { DeviceAsset } from '@/types/assets';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

type WipeDeviceModalProps = {
  deviceAsset: DeviceAsset;
  employeeName: string;
  onCompleteWipe: () => void;
  onCancel: () => void;
};

const WipeDeviceModal = ({
  deviceAsset,
  employeeName,
  onCompleteWipe,
  onCancel,
}: WipeDeviceModalProps) => {
  const { t } = useTranslation('people');
  const organizationId = useGetOrganizationId();
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [wipeDevice, { isLoading }] = useWipeDeviceCompat();

  const serialNumber = deviceAsset.serialNumber;

  const handleInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = event => {
    const newValue = event.target.value;
    setButtonEnabled(newValue.toLowerCase() === serialNumber?.toLowerCase());
  };

  const handleWipeDevice = async () => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID for device not found');
      }
      await wipeDevice({
        organizationId,
        deviceId: deviceAsset.device.id || '',
      }).unwrap();
      showSuccessToast({
        id: 'wipe-device-success-toast',
        message: employeeName
          ? t('employeeProfile.modals.wipeDevice.successMessageWithEmployee', {
              employeeName,
            })
          : t(
              'employeeProfile.modals.wipeDevice.successMessageWithoutEmployee'
            ),
      });
      onCompleteWipe();
    } catch (error) {
      showErrorToast({
        id: 'wipe-device-error-toast',
        message: t('employeeProfile.modals.wipeDevice.errorMessage'),
      });
    }
  };

  return (
    <ModalV2 visible hide={onCancel} ariaLabelledby="wipe-device">
      <ModalHeader>
        <Text variant="heading-2">
          {employeeName
            ? t('employeeProfile.modals.wipeDevice.titleWithEmployee', {
                employeeName,
              })
            : t('employeeProfile.modals.wipeDevice.titleWithoutEmployee')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text>
          {employeeName
            ? t('employeeProfile.modals.wipeDevice.bodyWithEmployee', {
                employeeName,
              })
            : t('employeeProfile.modals.wipeDevice.bodyWithoutEmployee')}
        </Text>
        <Spacer />
        <Text>
          {t('employeeProfile.modals.wipeDevice.confirmInstructions')}
        </Text>
        <Spacer />
        <FieldLabel>
          <Text color={ColorsV2.ERROR} pr="1">
            *
          </Text>
          <Text variant="label-small">
            {t('employeeProfile.modals.wipeDevice.serialNumberLabel')}
          </Text>
          <Text intent="secondary" variant="label-small" fontWeight="normal">
            {serialNumber}
          </Text>
          <Text variant="label-small">{`)`}</Text>
        </FieldLabel>
        <TextInput
          autoComplete="off"
          id="wipe-device-serial-number-input"
          placeholder="Serial #"
          onChange={handleInputChange}
        />
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="wipe-device-cancel"
            onClick={onCancel}
            variant="text"
            loading={isLoading}>
            {t('employeeProfile.modals.wipeDevice.cancel')}
          </ModalAction>
          <ModalAction
            id="wipe-device-submit"
            onClick={handleWipeDevice}
            disabled={!buttonEnabled}
            loading={isLoading}
            intent="danger">
            {t('employeeProfile.modals.wipeDevice.confirm')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default WipeDeviceModal;
